import { useState } from "react";

import { every, keys, map, max, sumBy, uniq } from "lodash/fp";

import Typography from "@mui/material/Typography";

import type { SettingsModuleProps } from "@app/analytics/account/types";
import WorkspaceSettingsAccordion from "@app/analytics/account/WorkspaceSettings/components/WorkspaceSettingsAccordion";
import type { IntegrationAccessSetting } from "@app/common/types";
import { pluralize, pluralizePhrase } from "@app/common/utils";
import type {
  EnterpriseWorkspaceUserIntegrationCounts,
  UpdateWorkspaceSettingsBody,
} from "@app/common/workspaces/types";
import { useAdminWorkspace } from "@app/common/workspaces/useWorkspace";

import ConfirmationModal from "@rui/ConfirmationModal";
import InfoPanel from "@rui/InfoPanel";

import {
  ALL_INTEGRATION_SETTINGS,
  INTEGRATION_CATEGORIES,
  WORKSPACE_SETTING_TO_PROVIDER_ID,
} from "./constants";
import IntegrationCategory from "./IntegrationCategory";

interface ToggleSettingsOpts {
  settings: IntegrationAccessSetting[];
  enable: boolean;
}

const getSumConnectedUsersBySettings = (
  settings: IntegrationAccessSetting[],
  userIntegrationCounts: EnterpriseWorkspaceUserIntegrationCounts
) =>
  sumBy(
    (s) => userIntegrationCounts[WORKSPACE_SETTING_TO_PROVIDER_ID[s]] ?? 0,
    settings
  );

const DisableSettingsConfirmation = ({
  pendingUpdate,
  onClose,
  onConfirm,
  userIntegrationCounts,
}: {
  pendingUpdate: UpdateWorkspaceSettingsBody;
  onClose: () => void;
  onConfirm: () => void;
  userIntegrationCounts: EnterpriseWorkspaceUserIntegrationCounts;
}) => {
  const updatedSettings = keys(pendingUpdate) as IntegrationAccessSetting[];
  const numProvidersDisabled = uniq(
    map((s) => WORKSPACE_SETTING_TO_PROVIDER_ID[s], updatedSettings)
  ).length;

  const numConnections = max(
    map(
      (s) => userIntegrationCounts[WORKSPACE_SETTING_TO_PROVIDER_ID[s]] ?? 0,
      updatedSettings
    )
  );

  return (
    <ConfirmationModal
      open
      onClose={onClose}
      onConfirm={onConfirm}
      title="Disable for Workspace?"
    >
      <Typography variant="bodyPrimaryReg" color="shades.neutral.600">
        {numProvidersDisabled > 1
          ? `${numConnections} or more Workspace members are`
          : `${pluralizePhrase(numConnections ?? 0, "Workspace member is", { plural: "Workspace members are" })}`}{" "}
        using{" "}
        {pluralize(
          "this integration",
          "these integrations",
          numProvidersDisabled
        )}
        . Disabling will revoke their authorization to use it with Read.
      </Typography>
    </ConfirmationModal>
  );
};

const IntegrationsModule = ({
  settings,
  onSettingUpdate,
  workspaceIsInactive,
}: SettingsModuleProps) => {
  const { workspace } = useAdminWorkspace();
  const [pendingUpdate, setPendingUpdate] =
    useState<UpdateWorkspaceSettingsBody | null>(null);

  const allIntegrationsDisabled = every(
    (setting) => !settings[setting],
    ALL_INTEGRATION_SETTINGS
  );

  // only need the default value for backwards compatibility until the BE is out
  const userIntegrationCounts =
    workspace.userIntegrationCounts ??
    ({} as EnterpriseWorkspaceUserIntegrationCounts);

  const handleToggle = (opts: ToggleSettingsOpts) => {
    let requireConfirmation = false;

    const settingUpdates = opts.settings.reduce((obj, setting) => {
      obj[setting] = opts.enable;
      return obj;
    }, {} as UpdateWorkspaceSettingsBody);

    if (!opts.enable) {
      requireConfirmation =
        getSumConnectedUsersBySettings(opts.settings, userIntegrationCounts) >
        0;
    }

    if (requireConfirmation) {
      setPendingUpdate(settingUpdates);
      return;
    }

    onSettingUpdate(settingUpdates);
  };

  const handleConfirmDisable = () => {
    if (!pendingUpdate) return;
    onSettingUpdate(pendingUpdate);
    setPendingUpdate(null);
  };

  return (
    <>
      <WorkspaceSettingsAccordion
        title="Integrations"
        subtitle="Customize access to messaging, email, CRM, and workflow integrations"
      >
        {allIntegrationsDisabled && (
          <InfoPanel severity="error" fullWidth>
            You have disabled all integrations for your Workspace. Read strongly
            recommends at least one integration to preserve functionality.
          </InfoPanel>
        )}
        {INTEGRATION_CATEGORIES.map((category) => (
          <IntegrationCategory
            key={category.id}
            category={category}
            disabled={workspaceIsInactive}
            integrationCounts={userIntegrationCounts}
            onToggle={handleToggle}
            settings={settings}
          />
        ))}
      </WorkspaceSettingsAccordion>
      {pendingUpdate && (
        <DisableSettingsConfirmation
          pendingUpdate={pendingUpdate}
          onClose={() => setPendingUpdate(null)}
          onConfirm={handleConfirmDisable}
          userIntegrationCounts={userIntegrationCounts}
        />
      )}
    </>
  );
};

export default IntegrationsModule;
